import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { promptFieldService } from '../../../services/promptFieldService';

const JSONImportPanel = ({ onRefresh }) => {
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importMode, setImportMode] = useState('paste'); // 'paste' or 'upload'

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.name?.endsWith('.json')) {
      setImportError('Please upload a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e?.target?.result;
        // Validate JSON
        JSON.parse(content);
        setImportJson(content);
        setImportError(null);
      } catch (err) {
        setImportError('Invalid JSON file format');
      }
    };
    reader.onerror = () => {
      setImportError('Failed to read file');
    };
    reader?.readAsText(file);
  };

  const handleImport = async () => {
    try {
      setImportError(null);
      setImportSuccess(null);
      setLoading(true);

      if (!importJson?.trim()) {
        throw new Error('Please provide JSON data to import');
      }

      // Parse JSON
      let parsedData = JSON.parse(importJson);
      
      // Handle array format - extract first element
      if (Array.isArray(parsedData)) {
        if (parsedData?.length === 0) {
          throw new Error('JSON array is empty');
        }
        parsedData = parsedData?.[0];
      }
      
      // Validate structure
      if (!parsedData?.useCase || !parsedData?.aiTools || !Array.isArray(parsedData?.aiTools)) {
        throw new Error('Invalid JSON structure. Expected format: { useCase: "...", aiTools: [...] }');
      }

      if (parsedData?.aiTools?.length === 0) {
        throw new Error('No AI tools found in JSON data');
      }

      // Call import service
      const result = await promptFieldService?.importPromptFieldsForUseCaseAndTool(parsedData);
      
      setImportSuccess(result?.message || 'Data imported successfully!');
      setImportJson('');
      
      // Refresh parent data
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 1000);
      }
    } catch (err) {
      console.error('Import error:', err);
      setImportError(err?.message || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setImportJson('');
    setImportError(null);
    setImportSuccess(null);
  };

  const exampleJson = `[
  {
    "useCase": "Image",
    "aiTools": [
      {
        "toolName": "ChatGPT",
        "promptFields": [
          {
            "fieldName": "Subject Description",
            "fieldLabel": "Subject Description",
            "fieldType": "TextArea",
            "order": 1,
            "placeholder": "Describe the main subject...",
            "description": "Main subject or focus",
            "required": true,
            "dropdownOptions": []
          },
          {
            "fieldName": "Art Style",
            "fieldLabel": "Art Style",
            "fieldType": "Select",
            "order": 2,
            "placeholder": "e.g., watercolor, 3D",
            "description": "Artistic style",
            "required": false,
            "dropdownOptions": [
              {"value": "watercolor", "label": "Watercolor", "order": 1},
              {"value": "3d", "label": "3D Render", "order": 2}
            ]
          }
        ]
      }
    ]
  }
]`;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Import JSON Data</h2>
        <p className="text-muted-foreground">
          Import prompt field configurations by pasting JSON data or uploading a JSON file.
          This will override existing data for the specified Use Case and AI Tool.
        </p>
      </div>

      {/* Import Mode Toggle */}
      <div className="mb-6">
        <div className="inline-flex rounded-lg border border-border p-1 bg-muted/50">
          <button
            onClick={() => setImportMode('paste')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              importMode === 'paste' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="FileText" size={16} className="inline mr-2" />
            Paste JSON
          </button>
          <button
            onClick={() => setImportMode('upload')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              importMode === 'upload' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Upload" size={16} className="inline mr-2" />
            Upload File
          </button>
        </div>
      </div>

      {/* Import Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Input Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {importMode === 'paste' ? 'JSON Data' : 'Upload JSON File'}
            </label>
            
            {importMode === 'paste' ? (
              <textarea
                value={importJson}
                onChange={(e) => setImportJson(e?.target?.value)}
                placeholder="Paste your JSON data here..."
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm resize-none"
              />
            ) : (
              <div>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="json-file-upload"
                  />
                  <label
                    htmlFor="json-file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Icon name="Upload" size={48} className="text-muted-foreground mb-4" />
                    <p className="text-foreground font-medium mb-1">Click to upload JSON file</p>
                    <p className="text-sm text-muted-foreground">or drag and drop</p>
                  </label>
                </div>
                {importJson && (
                  <div className="mt-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground">File loaded successfully</span>
                        <Icon name="CheckCircle" size={20} className="text-green-600" />
                      </div>
                      <textarea
                        value={importJson}
                        onChange={(e) => setImportJson(e?.target?.value)}
                        className="w-full h-64 px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-xs resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleImport}
              disabled={loading || !importJson?.trim()}
              iconName="Download"
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Importing...' : 'Import Data'}
            </Button>
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={loading}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>

          {/* Status Messages */}
          {importError && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-destructive mb-1">Import Failed</h4>
                  <p className="text-sm text-destructive/80">{importError}</p>
                </div>
              </div>
            </div>
          )}

          {importSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800 mb-1">Import Successful</h4>
                  <p className="text-sm text-green-700">{importSuccess}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example/Guide Section */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={20} />
              JSON Format Guide
            </h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div>
                <h4 className="font-medium text-foreground mb-2">Required Structure:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li><code className="bg-muted px-1 py-0.5 rounded">useCase</code> - Name of the use case</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">aiTools</code> - Array of AI tool configurations</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">toolName</code> - Name of the AI tool</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">promptFields</code> - Array of field definitions</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-2">Field Properties:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li><code className="bg-muted px-1 py-0.5 rounded">fieldName</code> - Internal field name</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">fieldLabel</code> - Display label</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">fieldType</code> - text, textarea, select, checkbox</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">order</code> - Display order (number)</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">required</code> - true/false</li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">dropdownOptions</code> - Array for select fields</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Code" size={20} />
              Example JSON
            </h3>
            <div className="relative">
              <pre className="bg-muted/50 rounded-lg p-4 text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto">
                {exampleJson}
              </pre>
              <button
                onClick={() => {
                  navigator?.clipboard?.writeText(exampleJson);
                }}
                className="absolute top-2 right-2 p-2 bg-card border border-border rounded-md hover:bg-muted transition-colors"
                title="Copy example"
              >
                <Icon name="Copy" size={16} />
              </button>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-800 mb-1">Warning</h4>
                <p className="text-sm text-yellow-700">
                  Importing data will <strong>delete all existing prompt fields</strong> for the specified Use Case and AI Tool combination, then create new fields from the JSON data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JSONImportPanel;